import { routingIsCurrent } from './assetObservation';
/**
 * thisDeviceModel — one state model for the This Device settings section.
 *
 * TCA-26100804. The readiness projection reports three separate fields —
 * state, cause and next action (TCA-26100708) — and this model keeps them
 * separate. Tone comes from the state, the sentence from the cause, and an
 * attention indicator from the next action. Deriving all three from the state
 * alone would re-flatten exactly what that work separated.
 *
 * Two rules the tests pin, because both are easy to lose in a redesign:
 *   - An unknown or absent cause yields no instruction. The projection
 *     deliberately returns a null cause rather than a guessed remedy, and the
 *     presentation must not substitute generic advice.
 *   - Only an action the operator performs themselves raises an attention
 *     indicator. Waiting is not a call to action.
 */
import {
  mediaReadinessPresentation,
  catalogMediaReadinessPresentation,
} from '@/utils/mediaReadinessPresentation';

export const THIS_DEVICE_TABS = Object.freeze([
  Object.freeze({ id: 'overview', label: 'Overview' }),
  Object.freeze({ id: 'order-data', label: 'Order data' }),
  Object.freeze({ id: 'media', label: 'Media' }),
  Object.freeze({ id: 'catalog', label: 'Product catalog' }),
  Object.freeze({ id: 'maps', label: 'Maps & routing' }),
  Object.freeze({ id: 'older-orders', label: 'Older orders' }),
]);

// Next actions the operator carries out. Anything else — waiting for the app,
// or no action at all — must not raise an attention indicator.
const OPERATOR_ACTIONS = new Set([
  'sign_in',
  'enable_offline_order_data',
  'review_media_storage',
  'review_device_status',
]);

export function operatorMustAct(nextAction) {
  return OPERATOR_ACTIONS.has(nextAction);
}

// Overview claims only what the runtime status establishes. A running service
// does not prove every area is ready, and an unqualified "ready for offline
// work" would contradict a delayed tab beside it (TCA-26100804).
const RUNTIME_SUMMARY = Object.freeze({
  ready: Object.freeze({ summary: 'Services running', tone: 'green' }),
  starting: Object.freeze({ summary: 'Starting', tone: 'amber' }),
  recovering: Object.freeze({ summary: 'Recovering', tone: 'amber' }),
  rebuild_required: Object.freeze({ summary: 'Needs attention', tone: 'red' }),
});

const ORDER_DATA_SUMMARY = Object.freeze({
  checking: Object.freeze({ summary: 'Checking', tone: 'quiet' }),
  downloading: Object.freeze({ summary: 'Downloading', tone: 'quiet' }),
  promoting: Object.freeze({ summary: 'Saving', tone: 'quiet' }),
  saving: Object.freeze({ summary: 'Saving', tone: 'quiet' }),
  resolving_invalidation: Object.freeze({ summary: 'Updating', tone: 'quiet' }),
  verifying: Object.freeze({ summary: 'Verifying', tone: 'quiet' }),
  catching_up: Object.freeze({ summary: 'Catching up', tone: 'quiet' }),
  not_enabled: Object.freeze({ summary: 'Not enabled', tone: 'quiet' }),
  offline: Object.freeze({ summary: 'Offline', tone: 'quiet' }),
  session_required: Object.freeze({ summary: 'Sign-in needed', tone: 'amber', attention: true }),
  delayed: Object.freeze({ summary: 'Delayed', tone: 'amber', attention: true }),
  failed: Object.freeze({ summary: 'Delayed', tone: 'amber', attention: true }),
  rebuild_required: Object.freeze({ summary: 'Rebuild needed', tone: 'amber', attention: true }),
  investigation_required: Object.freeze({ summary: 'Needs attention', tone: 'red', attention: true }),
});

// A failed check does not establish package damage, absence or freshness.
function assetSummary(map, routing, mapPhase, routingPhase) {
  if (mapPhase !== 'ready' || routingPhase !== 'ready') return { summary: null, tone: 'quiet', attention: false };
  // This tab covers two packages, so "Up to date" is a claim about both. It is
  // only made when both are known: a current map says nothing about routing
  // whose status has not been read (TCA-26100804).

  if (map?.status === 'missing_local') {
    return { summary: 'No offline map installed', tone: 'amber', attention: true };
  }
  if (map?.status === 'update_available'
    || routing?.local?.state === 'not_installed'
    || (routing?.checked === true && routing?.updateAvailable === true)) {
    return { summary: 'Update available', tone: 'amber', attention: false };
  }
  if (map?.status === 'current' && map?.local?.installed === true && routingIsCurrent(routing)) {
    return { summary: 'Up to date', tone: 'green', attention: false };
  }
  // Nothing truthful to summarise yet; the tab itself reports each package.
  return { summary: null, tone: 'quiet', attention: false };
}

function readinessTab(presentation, status) {
  const act = operatorMustAct(status?.nextAction);
  return {
    summary: presentation.label,
    // The sentence already resolves from the cause; never substitute advice
    // when the projection declined to supply one.
    detail: presentation.detail,
    tone: presentation.tone === 'green' ? 'green'
      : presentation.tone === 'red' ? 'red'
        : presentation.tone === 'amber' ? 'amber' : 'quiet',
    attention: act,
    nextAction: status?.nextAction ?? null,
  };
}

/**
 * The page-wide problem. When offline services are not running every tab shows
 * the same danger message, so it is seen whichever tab is open. The sentence is
 * keyed on the cause: restarting the app does not resolve a storage fault.
 */
export function pageWideProblem(runtime) {
  if (!runtime || runtime.state === 'ready') return null;
  if (runtime.state === 'starting' || runtime.state === 'recovering') return null;
  const storage = runtime.state === 'rebuild_required';
  return Object.freeze({
    tone: 'red',
    label: 'Offline services aren’t running',
    detail: storage
      ? 'Protected offline storage on this computer needs attention. Data already stored here is not deleted.'
      : 'This computer can’t work offline right now. Data already stored here is not deleted.',
    nextAction: 'review_device_status',
  });
}

export function buildThisDeviceModel({
  mapPhase = 'ready', routingPhase = 'ready',
  runtime = null,
  media = null,
  mediaLoading = false,
  catalog = null,
  catalogOwn = null,
  orderData = null,
  map = null,
  routing = null,
  historicalRangeCount = null,
} = {}) {
  const override = pageWideProblem(runtime);

  const mediaView = readinessTab(
    mediaReadinessPresentation(media, { loading: mediaLoading, desktop: true }),
    media,
  );
  const catalogView = readinessTab(catalogMediaReadinessPresentation(catalog), catalog);

  const orderState = orderData?.operationState || orderData?.state || null;
  // With no status in hand the navigation claims nothing. The card inside the
  // tab remains authoritative until its state is lifted (TCA-26100804 Slice 3).
  const orderBase = ORDER_DATA_SUMMARY[orderState]
    || { summary: null, tone: 'quiet' };

  const runtimeBase = RUNTIME_SUMMARY[runtime?.state]
    || { summary: 'Offline work unavailable', tone: 'red' };

  const assets = assetSummary(map, routing, mapPhase, routingPhase);

  const tabs = {
    overview: {
      summary: runtimeBase.summary,
      tone: runtimeBase.tone === 'green' ? 'green' : runtimeBase.tone,
      // Overview never carries an indicator: its status slot already covers
      // everything the other tabs summarise.
      attention: false,
    },
    'order-data': {
      summary: orderBase.summary,
      tone: orderBase.tone,
      attention: Boolean(orderBase.attention),
    },
    media: { summary: mediaView.summary, tone: mediaView.tone, attention: mediaView.attention },
    catalog: {
      // The tab is "Product catalog", so its summary describes the catalogue —
      // not its reference images, which are one part of it. Reporting the image
      // state here named a different subject than the tab's own status slot
      // (TCA-26100804). The indicator still rises for either, because either
      // can need the operator.
      summary: catalogOwn?.label ?? null,
      tone: catalogOwn?.tone ?? 'quiet',
      attention: Boolean(catalogOwn?.attention) || catalogView.attention,
    },
    maps: { summary: assets.summary, tone: assets.tone, attention: assets.attention },
    'older-orders': {
      // An unread count is not a confirmed zero. "Nothing kept yet" is reserved
      // for a count actually reported as 0; otherwise the navigation says
      // nothing rather than reassuring the operator wrongly.
      summary: Number.isInteger(historicalRangeCount)
        ? (historicalRangeCount > 0
          ? `${historicalRangeCount} ${historicalRangeCount === 1 ? 'range' : 'ranges'} kept`
          : 'Nothing kept yet')
        : null,
      tone: 'quiet',
      attention: false,
    },
  };

  // A page-wide problem pauses every other tab. Their own summaries are no
  // longer meaningful while the services are down.
  if (override) {
    for (const id of Object.keys(tabs)) {
      if (id === 'overview') continue;
      tabs[id] = { summary: 'Paused', tone: 'red', attention: false };
    }
  }

  return Object.freeze({
    override,
    tabs: Object.freeze(tabs),
    media: mediaView,
    catalog: catalogView,
  });
}
